import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeckBuildActivity from '../deck/DeckBuildActivity';
import { readTemplateResponse, startTemplateDeckPolling } from '../deck/templateDeckPolling.mjs';
import './deckBuilder.css';

// Lane 1405: the deck builder from chat (Ryan's approved designs, 2026-10-10).
// v2 steps: Supplier, Goal, Asks, Standard slides, From your chat, Build. One
// small AI read fills them in when the pop-up first opens (the server checks
// every suggestion); Build makes no AI call and turns the same pop-up into the
// live build view. The buyer's choices are saved as a draft, so a reopened
// pop-up resumes where it was.

// Lane 1405 v3: "Standard slides" (Ryan's wording), one label everywhere.
const STANDARD_SLIDES = 'Standard slides';
const STEPS = ['Supplier', 'Goal', 'Asks', STANDARD_SLIDES, 'From your chat', 'Build'];
const BUILD_STEP = STEPS.length + 1;
const DRAFT_DELAY_MS = 600;
const PREFILL_TIMEOUT_MS = 60000;
// A read another open pop-up started: ask again this often until it ends.
const PREFILL_POLL_MS = 2500;
const TOUCHABLE = ['supplier', 'goal', 'stage', 'asks'];

// The choices the buyer made, from a saved draft. A draft saved before this
// list existed counts every saved choice as the buyer's (as it was then).
function draftTouched(draft) {
  if (!draft) return new Set();
  if (Array.isArray(draft.touched)) return new Set(draft.touched.filter(t => TOUCHABLE.includes(t)));
  const out = new Set(['stage']);
  if (draft.supplier_id) out.add('supplier');
  if (draft.goal) out.add('goal');
  if (draft.asks_edited) out.add('asks');
  return out;
}
const NUMERIC = /^[+\-$(]?[\d$.,%()+\- ]+(of [\d,]+)?[%)]?$/;

const requestPath = id => `/api/deck-builder-requests/${id}`;
const POST_TIMEOUT_MS = 120000;

async function readJson(response, fallback) {
  let value = null;
  try { value = await response.json(); } catch { value = null; }
  if (!response.ok) {
    const detail = value?.detail;
    const error = new Error(typeof detail === 'string' ? detail : detail?.message || fallback);
    error.status = response.status;
    throw error;
  }
  return value;
}

function previewNote(table) {
  if (table.total_rows <= table.preview_rows.length) return `All ${table.total_rows} rows shown.`;
  const head = `Preview of the first ${table.preview_rows.length} of ${table.total_rows} rows.`;
  if (table.blocked) return head;
  return table.shown_rows < table.total_rows
    ? `${head} The first ${table.shown_rows} rows go in the deck.`
    : `${head} The whole table goes in the deck.`;
}

// "Reversing the increase" reads "reversing the increase" inside a sentence (an
// acronym such as "PPI" keeps its capitals).
function sentenceCase(text) {
  const value = String(text || '').replace(/[.\s]+$/, '');
  return /^[A-Z][a-z]/.test(value) ? value[0].toLowerCase() + value.slice(1) : value;
}

function SupplierStep({ view, onPick, authFetch, loading, about }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const chosen = view.supplier?.chosen;
  const all = useMemo(() => {
    const list = [...(view.supplier?.options || [])];
    if (chosen && !list.some(o => o.supplier_id === chosen.supplier_id)) list.unshift(chosen);
    return list;
  }, [view.supplier, chosen]);
  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setResults([]); return undefined; }
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const value = await readJson(await authFetch(`/api/suppliers/search?q=${encodeURIComponent(text)}&limit=8`),
          'The search did not work.');
        if (active) setResults(Array.isArray(value?.results) ? value.results : []);
      } catch { if (active) setResults([]); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [query, authFetch]);
  return <div>
    {chosen && <div className="dkbl-intro">Based on your conversation, it looks like you want a deck
      for <b>{chosen.supplier_name}</b>{about ? <> about <b>{sentenceCase(about)}</b>.</>
        : (chosen.supplier_name.endsWith('.') ? '' : '.')} Confirm a few things, then Wingman builds it.</div>}
    <h3 className="dkbl-h3">Who is this deck for?</h3>
    <p className="dkbl-sub">{view.supplier?.certain ? 'Picked from your chat. Change it if needed.'
      : 'Pick the supplier for this deck.'}</p>
    {all.length > 0 && <select className="dkbl-select" aria-label="Supplier" disabled={loading}
      value={chosen ? String(chosen.supplier_id) : ''}
      onChange={event => event.target.value && onPick(Number(event.target.value))}>
      {!chosen && <option value="">Choose a supplier</option>}
      {all.map(option => <option key={option.supplier_id} value={option.supplier_id}>{option.supplier_name}</option>)}
    </select>}
    <label className="dkbl-search-label" htmlFor="dkbl-search">{all.length ? 'Or search for another supplier' : 'Search for the supplier'}</label>
    <input id="dkbl-search" className="dkbl-search" type="search" value={query} placeholder="Type a supplier name"
      onChange={event => setQuery(event.target.value)} autoComplete="off" />
    {results.length > 0 && <ul className="dkbl-results" aria-label="Matching suppliers">
      {results.map(result => <li key={result.supplier_id}>
        <button type="button" onClick={() => { setQuery(''); setResults([]); onPick(result.supplier_id); }}>
          {result.supplier_name}</button></li>)}
    </ul>}
  </div>;
}

function TableRow({ table, picked, onToggle, note, offers = [], replacedPage = null, onReplace }) {
  const blocked = Boolean(table.blocked);
  const toggle = () => { if (!blocked) onToggle(table.table_id); };
  return <div className={`dkbl-trow${blocked ? ' is-off' : ''}${picked ? ' is-sel' : ''}`}
    onClick={toggle} role="presentation">
    <input type="checkbox" checked={picked} disabled={blocked} onChange={toggle}
      onClick={event => event.stopPropagation()} aria-label={`Include ${table.title}`} />
    <div className="dkbl-tbody">
      <div className="dkbl-ttitle">{table.title}</div>
      {note && !table.blocked && <p className="dkbl-tnote">{note}</p>}
      {/* Lane 1405 v3: the chat's own warning about this table (PLAN section 6). */}
      {table.chat_warning && !table.blocked && <p className="dkbl-twarn">Your chat noted: {table.chat_warning}</p>}
      {/* Lane 1405 v3: the same kind of data as a standard slide; the buyer decides
          (off by default, PLAN section 5). */}
      {picked && !blocked && offers.map(offer => <label key={offer.page} className="dkbl-replace"
        onClick={event => event.stopPropagation()}>
        <input type="checkbox" checked={replacedPage === offer.page}
          onChange={event => onReplace(event.target.checked ? offer.page : null)} />
        Use this table instead of the standard slide "{offer.name}"</label>)}
      <div className="dkbl-prev-wrap">
        <table className="dkbl-prev">
          <thead><tr>{table.columns.map((column, index) => <th key={index}>{column}</th>)}</tr></thead>
          <tbody>{table.preview_rows.map((row, r) => <tr key={r}>{row.map((cell, c) =>
            <td key={c} className={c > 0 && NUMERIC.test(cell) ? 'num' : ''}>{cell}</td>)}</tr>)}</tbody>
        </table>
      </div>
      <div className="dkbl-prev-note">{previewNote(table)}</div>
    </div>
    {blocked ? <span className="dkbl-badge">{table.blocked}</span>
      : <span className="dkbl-adds">adds {table.slides} slide{table.slides === 1 ? '' : 's'}</span>}
  </div>;
}

function TablesStep({ view, picked, onToggle, notes, goal, replaced, onReplace }) {
  // A picked table may replace one of the goal's standard slides of the same kind.
  const offersFor = table => (goal?.pages || []).filter(page => page.data_kind && page.data_kind === table.kind
    && !replaced.some(r => r.page === page.page && r.table_id !== table.table_id));
  const replacedFor = table => replaced.find(r => r.table_id === table.table_id)?.page || null;
  // Tables you can pick come first (newest question first); greyed ones go to
  // the bottom (Ryan, 2026-10-11).
  const all = view.groups || [];
  const groups = [
    ...all.map(g => ({ ...g, key: g.turn_id, tables: g.tables.filter(t => !t.blocked) })),
    ...all.map(g => ({ ...g, key: `${g.turn_id}-off`, tables: g.tables.filter(t => t.blocked) })),
  ].filter(g => g.tables.length);
  return <div>
    <h3 className="dkbl-h3">Add pages from your chat</h3>
    <p className="dkbl-sub">These tables came up in your conversation. Tick any you want in the deck as their own slides.</p>
    {groups.length === 0 && <p className="dkbl-empty">This chat has no tables yet. The deck uses the standard slides for your goal.</p>}
    {groups.map(group => <div className="dkbl-group" key={group.key}>
      <p className="dkbl-group-q">You asked: <b>{group.question}</b></p>
      {group.tables.map(table => <TableRow key={table.table_id} table={table}
        picked={picked.includes(table.table_id)} onToggle={onToggle} note={notes[table.table_id]}
        offers={offersFor(table)} replacedPage={replacedFor(table)}
        onReplace={page => onReplace(table.table_id, page)} />)}
    </div>)}
  </div>;
}

// Lane 1411: the deck's own price direction, shown under a pricing goal when
// net prices fell (the server writes the line from the deck's figures, and only
// for the supplier it read).
function GoalStep({ goals, goal, onPick, priceNote }) {
  const showNote = Boolean(priceNote && (priceNote.goals || []).includes(goal));
  return <div>
    <h3 className="dkbl-h3">What is your goal for this meeting?</h3>
    <p className="dkbl-sub">Picked from your chat when it is clear. Change it if needed.</p>
    <div className="dkbl-goals" role="radiogroup" aria-label="Meeting goal">
      {goals.map(g => <button key={g.id} type="button" role="radio" aria-checked={g.id === goal}
        className={`dkbl-goal${g.id === goal ? ' is-sel' : ''}`} onClick={() => onPick(g.id)}>
        <span className="dkbl-goal-head"><i aria-hidden="true" />{g.name}</span><p>{g.text}</p></button>)}
    </div>
    {showNote && <p className="dkbl-note" role="note">{priceNote.text}</p>}
  </div>;
}

function AsksStep({ supplier, goal, asks, stage, stages, askMax, maxAsks, onAsks, onStage, deckFigures }) {
  const set = (index, text) => onAsks(asks.map((a, i) => (i === index ? text.replace(/\s*\n\s*/g, ' ') : a)));
  return <div>
    <h3 className="dkbl-h3">What do you want to ask {supplier} to do?</h3>
    <p className="dkbl-sub">Written from your chat for "{goal.name}". Edit any ask, or write your own if it is different. These become the deck's asks, word for word.</p>
    {deckFigures && <p className="dkbl-note" role="note">{deckFigures.text}</p>}
    <div className="dkbl-asks">{asks.map((ask, index) => <div className="dkbl-ask-row" key={index}>
      <span className="dkbl-ask-n">{index + 1}</span>
      <div><textarea aria-label={`Ask ${index + 1}`} value={ask} maxLength={askMax}
        onChange={event => set(index, event.target.value)} />
        {ask.length > askMax - 20 && <span className="dkbl-count">{ask.length} of {askMax}</span>}</div>
      {asks.length > 1 ? <button type="button" className="dkbl-rm" aria-label={`Remove ask ${index + 1}`}
        onClick={() => onAsks(asks.filter((_, i) => i !== index))}>&times;</button> : <span />}
    </div>)}</div>
    {asks.length < maxAsks && <button type="button" className="dkbl-link" onClick={() => onAsks([...asks, ''])}>+ Add an ask</button>}
    <p className="dkbl-label">Have you raised this with them before?</p>
    <div className="dkbl-seg" role="group" aria-label="Meeting stage">
      {stages.map(option => <button key={option.id} type="button" aria-pressed={option.id === stage}
        className={option.id === stage ? 'is-on' : ''} onClick={() => onStage(option.id)}>{option.name}</button>)}
    </div>
  </div>;
}

function SlidesStep({ goal }) {
  return <div>
    <h3 className="dkbl-h3">{STANDARD_SLIDES} included for "{goal.name}"</h3>
    <p className="dkbl-sub">{goal.lead} Next, add pages from your chat.</p>
    <ul className="dkbl-slides">{goal.pages.map(page => <li key={page.name}>
      <span className="dkbl-ck" aria-hidden="true">&#10003;</span><b>{page.name}</b>
      <span className="dkbl-d">{page.line}{page.when ? `, ${page.when}` : ''}</span></li>)}</ul>
    <p className="dkbl-small">A slide whose data is too thin for this supplier, or that would support the supplier's side, is left out.</p>
  </div>;
}

function SummaryStep({ view, goal, stage, asks, pickedTables, replaced = [] }) {
  const slides = pickedTables.reduce((sum, t) => sum + (t.slides || 0), 0);
  const replacedPages = new Set(replaced.map(r => r.page));
  const standard = goal.pages.filter(page => !replacedPages.has(page.page));
  return <div className="dkbl-sum">
    <h3 className="dkbl-h3">Ready to build</h3>
    <p className="dkbl-sub">This is what goes in your deck.</p>
    <div className="dkbl-sum-card"><div className="dkbl-sum-label">Supplier</div>
      <div className="dkbl-sum-value">{view.supplier?.chosen?.supplier_name}</div></div>
    <div className="dkbl-sum-card"><div className="dkbl-sum-label">Goal</div>
      <div className="dkbl-sum-value"><b>{goal.name}</b> · {stage?.name}</div></div>
    <div className="dkbl-sum-card"><div className="dkbl-sum-label">Asks</div>
      <ul className="dkbl-sum-list">{asks.map((ask, i) => <li key={i}><span>{i + 1}. {ask}</span></li>)}</ul></div>
    <div className="dkbl-sum-card"><div className="dkbl-sum-label">{STANDARD_SLIDES}</div>
      <div className="dkbl-chips">{standard.map(page => <span className="dkbl-chip" key={page.name}>{page.name}</span>)}</div></div>
    {replaced.length > 0 && <div className="dkbl-sum-card"><div className="dkbl-sum-label">Replaced by your chat tables</div>
      <ul className="dkbl-sum-list">{replaced.map(r => <li key={r.page}>
        <span>{goal.pages.find(page => page.page === r.page)?.name}: {pickedTables.find(t => t.table_id === r.table_id)?.title}</span>
      </li>)}</ul></div>}
    <div className="dkbl-sum-card">
      <div className="dkbl-sum-label">Tables from your chat{pickedTables.length ? ` (${slides} slide${slides === 1 ? '' : 's'})` : ''}</div>
      {pickedTables.length
        ? <ul className="dkbl-sum-list">{pickedTables.map(t => <li key={t.table_id}><span>{t.title}</span>
            <span className="dkbl-sum-count">{t.slides} slide{t.slides === 1 ? '' : 's'}</span></li>)}</ul>
        : <div className="dkbl-sum-value">None</div>}
    </div>
    <p className="dkbl-note">The write-up uses your chat, your goal and your asks.</p>
  </div>;
}

function BuildView({ deckVersionId, preparing, startedAt, authFetch, onOpened, onAnother }) {
  const [deck, setDeck] = useState(null);
  const [connection, setConnection] = useState(null);
  const [openError, setOpenError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!deckVersionId) return undefined;
    let active = true;
    setConnection(null);
    const stop = startTemplateDeckPolling(
      async () => (await readTemplateResponse(await authFetch(`/api/template-decks/${deckVersionId}`))).json(),
      async value => { if (active) setDeck(value); },
      (message, info) => { if (active) setConnection(message ? { message, ...info } : null); });
    return () => { active = false; stop(); };
  }, [deckVersionId, authFetch, attempt]);
  const open = async () => {
    setOpenError('');
    try {
      const result = deck?.composition_id ? deck
        : await readJson(await authFetch(`/api/template-decks/${deckVersionId}/open`, { method: 'POST' }),
          'The deck could not be opened.');
      if (!Number.isInteger(result.composition_id) || result.composition_id <= 0) throw new Error('The saved deck is unavailable.');
      onOpened(result.composition_id);
    } catch (failure) { setOpenError(failure.message); }
  };
  // While the server prepares the build (before it has a deck), the first stage is under way.
  const activity = preparing || !deck ? {
    phase: 'gathering', completed: [], health: 'active',
    milestones: [{ id: 'approved', at: new Date(startedAt).toISOString() }],
    elapsed_seconds: Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
  } : deck.activity;
  const finished = !preparing && ['ready', 'failed'].includes(deck?.status);
  return <>
    <DeckBuildActivity activity={activity} status={preparing ? 'queued' : deck?.status}
      connection={connection} openError={openError}
      onRetry={() => (openError ? open() : setAttempt(value => value + 1))} onOpen={open} />
    {/* Lane 1405 v3: a new deck for the same answer, once this one has finished. */}
    {finished && onAnother && <button type="button" className="dkbl-link dkbl-another" onClick={onAnother}>
      Build another deck</button>}
  </>;
}

function suggestedAsks(view, prefill, goal, supplierId) {
  // The AI read's asks were checked against what the chat says about ONE supplier.
  const own = prefill && prefill.asks_supplier_id && prefill.asks_supplier_id === supplierId;
  const fromRead = own ? prefill.asks?.[goal] : null;
  const fallback = view.goals.find(g => g.id === goal)?.default_asks || [];
  return [...(Array.isArray(fromRead) && fromRead.length ? fromRead : fallback)];
}

export default function DeckBuilderModal({ publicId, authFetch: rawFetch, onClose, onAnother = null }) {
  const navigate = useNavigate();
  // The chat page passes a new authFetch on every render; one stable function
  // keeps the pop-up from reloading (and resetting the supplier) on each render.
  const fetchRef = useRef(rawFetch);
  fetchRef.current = rawFetch;
  const authFetch = useCallback((...args) => fetchRef.current(...args), []);
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState(null);
  const [stage, setStage] = useState('first');
  const [asks, setAsks] = useState([]);
  const [asksEdited, setAsksEdited] = useState(false);
  const [picked, setPicked] = useState([]);
  // Lane 1405 v3: [{page, table_id}]: picked tables used instead of standard slides.
  const [replaced, setReplaced] = useState([]);
  const [ready, setReady] = useState(false);       // choices are set: drafts may be saved
  const [building, setBuilding] = useState(null); // {preparing, deckVersionId, startedAt}
  const [buildError, setBuildError] = useState('');
  const revision = useRef(0);
  const viewRef = useRef(null);
  const goalRef = useRef(null);         // the latest goal, for the AI read that returns later
  const asksEditedRef = useRef(false);
  // Lane 1405 v2 r3: what the buyer chose (saved with the draft), so the AI
  // read, which can return late or after a reopen, fills only the rest.
  const touched = useRef(new Set());
  goalRef.current = goal ?? goalRef.current;
  asksEditedRef.current = asksEdited;
  const dialog = useRef(null);

  const load = useCallback(async supplierId => {
    setLoading(true); setError('');
    try {
      const query = supplierId ? `?supplier_id=${supplierId}` : '';
      const value = await readJson(await authFetch(`${requestPath(publicId)}${query}`), 'The deck builder could not be opened.');
      setView(value);
      return value;
    } catch (failure) { setError(failure.message); return null; }
    finally { setLoading(false); }
  }, [publicId, authFetch]);

  // The pop-up's choices from the server: the saved draft first, then the last
  // build attempt's choices, then the AI read's suggestions, then the defaults.
  const hydrate = useCallback(value => {
    const read = value.prefill?.result || null;
    setPrefill(read);
    revision.current = value.draft_revision || 0;
    const draft = value.draft;
    const saved = value.choices;
    const supplierId = value.supplier?.chosen?.supplier_id || null;
    touched.current = draftTouched(draft);
    const startGoal = draft?.goal || saved?.goal || read?.goal || null;
    goalRef.current = startGoal;
    setGoal(startGoal);
    setStage((touched.current.has('stage') && draft?.stage) || saved?.stage || read?.stage
      || draft?.stage || 'first');
    let edited = false;
    if (Array.isArray(draft?.asks) && draft.asks.length) {
      edited = Boolean(draft.asks_edited);
      setAsks(draft.asks);
    } else if (Array.isArray(saved?.asks) && saved.asks.length) {
      edited = true;
      setAsks(saved.asks);
    } else setAsks(startGoal ? suggestedAsks(value, read, startGoal, supplierId) : []);
    setAsksEdited(edited);
    asksEditedRef.current = edited;     // read below before the next render
    const tables = draft?.table_ids || saved?.table_ids;
    setPicked(Array.isArray(tables) ? tables : []);
    const swaps = draft ? draft.replaced : saved?.replaced;
    setReplaced(Array.isArray(swaps) ? swaps : []);
    if (draft?.step && draft.step <= STEPS.length) setStep(draft.step);
    setReady(true);
    return read;
  }, []);

  // Opening: the saved choices, then the AI read in the background.
  useEffect(() => {
    let active = true;
    // POST the read; while another opening's read is still running, ask again
    // every PREFILL_POLL_MS until it ends or PREFILL_TIMEOUT_MS passes.
    const observeRead = async () => {
      setReading(true);
      const deadline = Date.now() + PREFILL_TIMEOUT_MS;
      try {
        while (active && Date.now() < deadline) {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), Math.max(1000, deadline - Date.now()));
          let reply;
          try {
            reply = await readJson(await authFetch(`${requestPath(publicId)}/prefill`,
              { method: 'POST', signal: controller.signal }), 'The chat could not be read.');
          } finally { clearTimeout(timer); }
          if (reply?.status !== 'running') return reply?.result || null;
          await new Promise(resolve => setTimeout(resolve, PREFILL_POLL_MS));
        }
        return null;
      } catch { return null; /* the pop-up still works with the plain defaults */ }
      finally { if (active) setReading(false); }
    };
    (async () => {
      let value = await load();
      if (!value || !active) return;
      if (value.status === 'built' && value.deck_version_id) {
        setBuilding({ preparing: false, deckVersionId: value.deck_version_id, startedAt: Date.now() }); setStep(BUILD_STEP);
        return;
      }
      if (value.status === 'building') {
        setBuilding({ preparing: true, deckVersionId: null, startedAt: Date.now() }); setStep(BUILD_STEP);
        return;
      }
      hydrate(value);
      const readStatus = value.prefill?.status;
      let fresh = null;
      if (readStatus === 'done' || readStatus === 'fallback') {
        // The read ended before this opening (Codex v2 r3 round 2): its
        // suggestions still fill what the buyer has not chosen, by the rules below.
        fresh = value.prefill?.result || null;
      } else if (value.status !== 'expired') {
        // The one AI read runs while the buyer starts (or is still running from an
        // earlier opening): its suggestions fill only what the buyer has not chosen.
        fresh = await observeRead();
        if (active && fresh) {
          // The table list now has the read's titles, kinds and warnings (Lane 1405
          // v3, Codex S4 r2): reload it for the supplier the pop-up shows now.
          const shown = (viewRef.current || value).supplier?.chosen?.supplier_id;
          if (shown) value = (await load(shown)) || value;
        }
      }
      if (!active || !fresh) return;
      // The buyer may have picked a supplier while the read ran: never replace it.
      // (viewRef is set when the pop-up draws; before that, the view just loaded.)
      if (fresh.supplier_id && !touched.current.has('supplier')
          && !(viewRef.current || value).supplier?.chosen) {
        value = (await load(fresh.supplier_id)) || value;
      }
      if (!active) return;
      setPrefill(fresh);
      if (!touched.current.has('stage') && fresh.stage) setStage(fresh.stage);
      const nextGoal = goalRef.current || fresh.goal || null;
      if (!goalRef.current && fresh.goal) { goalRef.current = fresh.goal; setGoal(fresh.goal); }
      if (nextGoal && !asksEditedRef.current) {
        // The supplier the buyer has now (it may have changed while the read ran).
        const current = viewRef.current || value;
        setAsks(suggestedAsks(current, fresh, nextGoal, current.supplier?.chosen?.supplier_id || null));
      }
    })();
    return () => { active = false; };
  }, [load, authFetch, publicId, hydrate]);

  // A build the server is still preparing (for example after a reload): watch the request.
  useEffect(() => {
    if (!building?.preparing || building.posting) return undefined;
    let active = true;
    const timer = setInterval(async () => {
      try {
        const value = await readJson(await authFetch(requestPath(publicId)), 'The build could not be loaded.');
        if (!active) return;
        if (value.status === 'built') setBuilding(b => ({ ...b, preparing: false, deckVersionId: value.deck_version_id }));
        else if (value.status === 'failed' || value.status === 'open') {
          // Back to the pop-up with every choice (also after a reopened build).
          setView(value); hydrate(value);
          setBuilding(null); setStep(STEPS.length); setBuildError('The build could not start. Try again.');
        }
      } catch { /* keep watching */ }
    }, 2500);
    return () => { active = false; clearInterval(timer); };
  }, [building, authFetch, publicId, hydrate]);

  const supplierId = view?.supplier?.chosen?.supplier_id || null;
  viewRef.current = view;

  // The draft is saved shortly after each change, so a reopened pop-up resumes here.
  // One save at a time; changes made while a save runs are saved right after it.
  const latestDraft = useRef(null);
  const saving = useRef(false);
  const queued = useRef(false);
  const adopt = useCallback(async detail => {
    // Another tab saved first: take all of its choices.
    const newer = detail.draft || {};
    revision.current = detail.draft_revision;
    let value = viewRef.current;
    if (newer.supplier_id && newer.supplier_id !== value?.supplier?.chosen?.supplier_id) {
      value = (await load(newer.supplier_id)) || value;
    }
    touched.current = draftTouched(newer);
    goalRef.current = newer.goal || null;
    setGoal(newer.goal || null); setStage(newer.stage || 'first');
    setAsks(newer.asks || []); setAsksEdited(Boolean(newer.asks_edited));
    setPicked(newer.table_ids || []);
    setReplaced(newer.replaced || []);
    if (newer.step && newer.step <= STEPS.length) setStep(newer.step);
  }, [load]);
  const flush = useCallback(async () => {
    if (saving.current) { queued.current = true; return; }
    saving.current = true;
    try {
      do {
        queued.current = false;
        const draft = latestDraft.current;
        if (!draft) break;
        const response = await authFetch(`${requestPath(publicId)}/draft`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ revision: revision.current, draft }) });
        const value = await response.json().catch(() => null);
        if (response.ok) revision.current = value?.draft_revision ?? revision.current;
        else if (response.status === 409 && value?.detail) { await adopt(value.detail); break; }
      } while (queued.current);
    } catch { /* a draft is a convenience; Build sends every choice itself */ }
    finally { saving.current = false; }
  }, [authFetch, publicId, adopt]);
  useEffect(() => {
    if (!ready || building || !view) return undefined;
    latestDraft.current = { step: Math.min(step, STEPS.length), supplier_id: supplierId, goal, stage, asks,
      asks_edited: asksEdited, table_ids: picked, replaced: validReplaced,
      touched: TOUCHABLE.filter(t => touched.current.has(t) || (t === 'asks' && asksEdited)) };
    const timer = setTimeout(flush, DRAFT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, building, view, step, supplierId, goal, stage, asks, asksEdited, picked, replaced, flush]);  // eslint-disable-line react-hooks/exhaustive-deps

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    dialog.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const allTables = useMemo(() => (view?.groups || []).flatMap(group => group.tables), [view]);
  const slideCount = picked.reduce((sum, id) => sum + (allTables.find(t => t.table_id === id)?.slides || 0), 0);
  const room = view?.table_slide_room ?? 15;
  const pickedTables = allTables.filter(t => picked.includes(t.table_id));
  const goalSpec = view?.goals?.find(g => g.id === goal) || null;
  const stageSpec = view?.stages?.find(s => s.id === stage) || null;
  const askMax = view?.ask_max ?? 120;
  const cleanAsks = asks.map(a => a.trim()).filter(Boolean);
  const asksOk = cleanAsks.length >= 1 && cleanAsks.every(a => a.length <= askMax);
  const notes = prefill?.table_notes || {};

  const pickSupplier = async id => {
    touched.current.add('supplier');
    const value = await load(id);
    if (!value) return;
    const blocked = new Set((value.groups || []).flatMap(g => g.tables).filter(t => t.blocked).map(t => t.table_id));
    setPicked(current => current.filter(x => !blocked.has(x)));
    if (!asksEdited && goal) setAsks(suggestedAsks(value, prefill, goal, id));
  };
  const pickGoal = id => {
    touched.current.add('goal');
    goalRef.current = id;
    setGoal(id);
    if (!asksEdited) setAsks(suggestedAsks(view, prefill, id, supplierId));
  };
  const editAsks = next => { setAsks(next); setAsksEdited(true); };
  const toggle = id => setPicked(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));
  const replace = (tableId, page) => setReplaced(current => [
    ...current.filter(r => r.table_id !== tableId && r.page !== page),
    ...(page ? [{ page, table_id: tableId }] : [])]);
  // A replacement stands only for a picked table of the page's kind, in this goal.
  const validReplaced = replaced.filter(r => picked.includes(r.table_id)
    && (goalSpec?.pages || []).some(page => page.page === r.page && page.data_kind
      && page.data_kind === allTables.find(t => t.table_id === r.table_id)?.kind));

  const build = async () => {
    setBuildError('');
    const startedAt = Date.now();
    setBuilding({ preparing: true, posting: true, deckVersionId: null, startedAt }); setStep(BUILD_STEP);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), POST_TIMEOUT_MS);
    try {
      const ordered = allTables.filter(t => picked.includes(t.table_id)).map(t => t.table_id);
      const response = await authFetch(`${requestPath(publicId)}/build`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
        body: JSON.stringify({ supplier_id: supplierId, table_ids: ordered, goal, stage, asks: cleanAsks,
          replaced: validReplaced }) });
      if (!response.ok && response.status < 500) {
        // A plain refusal (for example a table that names another supplier).
        await readJson(response, 'The build could not start. Try again.');
      }
      const result = response.ok ? await response.json() : null;
      if (result?.status === 'built' && result.deck_version_id) {
        setBuilding({ preparing: false, deckVersionId: result.deck_version_id, startedAt });
        return;
      }
      // No clear answer (a server error or a still-running build): the saved
      // request decides, through the watcher below.
      setBuilding({ preparing: true, deckVersionId: null, startedAt });
    } catch (failure) {
      if (failure.status && failure.status < 500) {
        setBuilding(null); setStep(STEPS.length); setBuildError(failure.message);
      } else {
        // A lost or timed-out response: the server may still have started the
        // build. Watch the saved request before saying anything failed.
        setBuilding({ preparing: true, deckVersionId: null, startedAt });
      }
    } finally {
      clearTimeout(timer);
    }
  };

  const stepsRow = <div className="dkbl-steps">{STEPS.map((name, index) => {
    const number = index + 1;
    const done = step > number;
    return <span key={name} className={step === number ? 'is-on' : done ? 'is-done' : ''}>
      <b aria-hidden="true">{done ? '✓' : number}</b>{name}</span>;
  })}</div>;
  const back = target => <button type="button" className="dkbl-btn" onClick={() => setStep(target)}>Back</button>;
  const next = (target, enabled = true) => <button type="button" className="dkbl-btn pri" disabled={!enabled || loading}
    onClick={() => setStep(target)}>Next</button>;

  let body = null; let foot = null;
  if (error && !view) {
    body = <p className="dkbl-error" role="alert">{error}</p>;
  } else if (!view || (!ready && !building)) {
    body = <p className="dkbl-sub">Loading your deck builder…</p>;
  } else if (view.status === 'expired') {
    body = <p className="dkbl-sub">This deck request expired. Ask for the deck again in chat.</p>;
  } else if (step === BUILD_STEP && building) {
    body = <BuildView deckVersionId={building.deckVersionId} preparing={building.preparing}
      startedAt={building.startedAt} authFetch={authFetch} onAnother={onAnother}
      onOpened={compositionId => { onClose(); navigate(`/decks/meeting-prep?composition=${compositionId}`); }} />;
  } else if (step === 1) {
    body = <>{reading && <p className="dkbl-note dkbl-reading">Reading your chat to suggest your goal and asks…</p>}
      <SupplierStep view={view} onPick={pickSupplier} authFetch={authFetch} loading={loading}
        about={prefill?.supplier_id === supplierId ? prefill?.about : ''} /></>;
    foot = <><span />{error && <span className="dkbl-error" role="alert">{error}</span>}{next(2, Boolean(supplierId))}</>;
  } else if (step === 2) {
    body = <>{reading && <p className="dkbl-note dkbl-reading">Still reading your chat to suggest a goal…</p>}
      <GoalStep goals={view.goals} goal={goal} onPick={pickGoal}
        priceNote={prefill?.pricing_note?.supplier_id === supplierId ? prefill.pricing_note : null} /></>;
    foot = <>{back(1)}<span />{next(3, Boolean(goalSpec))}</>;
  } else if (step === 3 && goalSpec) {
    body = <AsksStep supplier={view.supplier?.chosen?.supplier_name} goal={goalSpec} asks={asks} stage={stage}
      stages={view.stages} askMax={askMax} maxAsks={view.max_asks ?? 3} onAsks={editAsks}
      deckFigures={prefill?.deck_figures?.supplier_id === supplierId ? prefill.deck_figures : null}
      onStage={id => { touched.current.add('stage'); setStage(id); }} />;
    foot = <>{back(2)}<span />{next(4, asksOk)}</>;
  } else if (step === 4 && goalSpec) {
    body = <SlidesStep goal={goalSpec} />;
    foot = <>{back(3)}<span className="dkbl-note">{STANDARD_SLIDES}: <b>{goalSpec.pages.length}</b></span>{next(5)}</>;
  } else if (step === 5 && goalSpec) {
    body = <TablesStep view={view} picked={picked} onToggle={toggle} notes={notes} goal={goalSpec}
      replaced={validReplaced} onReplace={replace} />;
    foot = <>{back(4)}
      <span className="dkbl-note">Slides added from chat: <b>{slideCount}</b>
        {slideCount > room && <span className="dkbl-warn"> Too many table slides for one deck. Untick a table.</span>}</span>
      {next(6, slideCount <= room)}</>;
  } else if (goalSpec) {
    body = <SummaryStep view={view} goal={goalSpec} stage={stageSpec} asks={cleanAsks} pickedTables={pickedTables}
      replaced={validReplaced} />;
    foot = <>{back(5)}
      {buildError ? <span className="dkbl-error" role="alert">{buildError}</span> : <span />}
      <button type="button" className="dkbl-btn pri" disabled={!asksOk || !supplierId} onClick={build}>
        {buildError ? 'Try again' : 'Build deck'}</button></>;
  } else {
    body = <GoalStep goals={view.goals} goal={goal} onPick={pickGoal} />;
    foot = <>{back(1)}<span />{next(3, Boolean(goalSpec))}</>;
  }

  return <div className="dkbl-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="dkbl-modal" role="dialog" aria-modal="true" aria-labelledby="dkbl-title" tabIndex={-1} ref={dialog}>
      <div className="dkbl-head"><h2 id="dkbl-title">Deck builder</h2>
        <button type="button" className="dkbl-close" aria-label="Close" onClick={onClose}>&times;</button></div>
      {step < BUILD_STEP && stepsRow}
      {step === BUILD_STEP && <div className="dkbl-steps">{STEPS.map(name => <span key={name} className="is-done"><b aria-hidden="true">{'✓'}</b>{name}</span>)}</div>}
      <div className={`dkbl-body${step === BUILD_STEP ? ' is-build' : ''}`}>{body}</div>
      {foot && <div className="dkbl-foot">{foot}</div>}
    </div>
  </div>;
}

// Lane 1405 v3: the Build a deck button by the chat box (POST /api/deck-builder-requests).
// `another`: a new request for the same answer once a deck was built from it.
export async function openFromButton(authFetch, conversationId, another = false) {
  const value = await readJson(await authFetch('/api/deck-builder-requests', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversation_id: conversationId, ...(another ? { another: true } : {}) }) }),
  'The deck builder could not be opened.');
  return value.public_id;
}

// The pop-up for one request; "Build another deck" swaps in a new request for the
// chat's newest answer (needs the conversation).
export function DeckBuilderHost({ publicId, authFetch, onClose, conversationId = null }) {
  const [current, setCurrent] = useState(publicId);
  const [anotherError, setAnotherError] = useState('');
  useEffect(() => { setCurrent(publicId); }, [publicId]);
  const another = conversationId ? async () => {
    setAnotherError('');
    try { setCurrent(await openFromButton(authFetch, conversationId, true)); }
    catch (failure) { setAnotherError(failure.message); }
  } : null;
  return <>
    <DeckBuilderModal key={current} publicId={current} authFetch={authFetch} onClose={onClose} onAnother={another} />
    {anotherError && <p className="dkbl-error" role="alert">{anotherError}</p>}
  </>;
}

export function DeckBuilderEntry({ builder, authFetch, conversationId = null }) {
  const [open, setOpen] = useState(false);
  if (!builder?.public_id) return null;
  return <div className="dkbl-entry" data-testid="deck-builder-entry">
    {builder.show_line && <p>{builder.line}</p>}
    <button type="button" className="dkbl-start" onClick={() => setOpen(true)}>Open deck builder</button>
    {open && <DeckBuilderHost publicId={builder.public_id} authFetch={authFetch} conversationId={conversationId}
      onClose={() => setOpen(false)} />}
  </div>;
}
